import { useEffect, useState } from "react";
import { BlueBirdTheme, Breadcrumb, Typography } from "@kitbird/core";
import { useLocation, useParams, useHref } from "react-router-dom";
import { HeaderText } from "../../../components/FloatHeader/FloatHeader.style";
import {
  PageCard,
  PageWrapper,
} from "../../../components/Content/Content.style";
import useCreateReceiptSubmission from "./hooks/useCreateReceiptSubmission";
import SubmissionTable from "./SubmissionTable";
import SubmissionForm from "./SubmissionForm/SubmissionForm";
import ReceiptSubmissionApprovalModal from "./components/modals/ReceiptSubmissionApproval";
import SubmissionDetail from "./SubmissionDetail";
import { getDetailTrx, IGetDetailTrxResponse, IGetTrxData } from "../../../services/trxService";
import PageLoading from "../../../components/Loading/PageLoading";
import {
  PageType,
  ApprovalModalType,
} from "./enums";
import PrintModal from "./components/modals/PrintModal";

const { gray } = BlueBirdTheme.colors;

const CreateReceiptSubmission = () => {
  const location = useLocation();
  const urlParams = useParams();
  const [isOpen, setOpen] = useState<ApprovalModalType | false>(false);
  const [detailRow, setDetailRow] = useState<IGetTrxData>();
  const [detail, setDetail] = useState<IGetDetailTrxResponse>();
  const useCustomHook = useCreateReceiptSubmission();
  const {
    meta,
    isLoading,
    setLoading,
    fetchCount,
    methods,
    refetchTable,
    initialValueCharge,
    initFetch,
    setDestinationAccount,
    setGlAccount,
    pageType,
    setPageType,
  } = useCustomHook;

  useEffect(() => {
    if (location.pathname.includes("riwayat-pengajuan-bon")) setPageType(PageType.HISTORY_LIST);
    else setPageType(PageType.LIST);
  }, [location.pathname]);

  useEffect(() => {
    if (pageType === PageType.LIST || pageType === PageType.HISTORY_LIST) {
      methods.reset(initialValueCharge);
      initFetch();
      setDestinationAccount(undefined);
      setGlAccount(undefined);
    }
  }, [pageType]);

  useEffect(() => {
    if (urlParams.trx_id) {
      setDetailRow({ trx_id: urlParams.trx_id, extend_bm: urlParams.extend_bm || "0" } as never);
      setPageType(PageType.DETAIL);
    }
  }, [urlParams]);

  const fetchDetail = async (id: string, extendBm: string) => {
    setLoading(true);
    await getDetailTrx(id, extendBm).then((res) => {
      setDetail(res);
      setLoading(false);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (detailRow) fetchDetail(detailRow.trx_id, detailRow.extend_bm);
  }, [detailRow]);

  return (<>
    <PageWrapper>
      <HeaderText>
        <Typography level={3} color={gray.title} fontWeight="medium">
          {location.pathname.includes("riwayat-pengajuan-bon") ? "Riwayat pengajuan bon" : "Pengajuan bon"}
        </Typography>
      </HeaderText>
      {pageType === PageType.CREATE && (<>
        <Breadcrumb
          items={[
            { title: "Pengajuan bon", path: "LIST" },
            { title: "Tambah data" },
          ]}
          onClick={(item) => {
            if (item.path && item.path === "LIST") {
              refetchTable();
              setPageType(PageType.LIST);
              setDetailRow(undefined);
            }
          }}
        />
        <PageCard style={{ height: "80vh" }}>
          <SubmissionForm setPageType={setPageType} useCustomHook={useCustomHook} />
        </PageCard>
      </>)}
      {(pageType === PageType.LIST || pageType === PageType.HISTORY_LIST)
        && (
          <PageCard style={{ height: "85vh" }}>
            <SubmissionTable
              pageType={pageType}
              setPageType={setPageType}
              setOpen={setOpen}
              setDetailRow={setDetailRow}
              useCustomHook={useCustomHook}
            />
          </PageCard>
        )
      }
      {(pageType === PageType.DETAIL || pageType === PageType.HISTORY_DETAIL) && (
        <SubmissionDetail
          pageType={pageType}
          setPageType={setPageType}
          detailRow={detailRow}
          setDetailRow={setDetailRow}
          detail={detail}
          setDetail={setDetail}
          isApprovalModal={isOpen}
          setApprovalModal={setOpen}
          isLoading={isLoading}
          refetchTable={refetchTable}
        />
      )}
      <ReceiptSubmissionApprovalModal
        isApprovalModal={isOpen}
        setApprovalModal={setOpen}
        detailRow={detailRow}
        setDetailRow={setDetailRow}
        detail={detail}
        setDetail={setDetail}
        useCustomHook={useCustomHook}
        fetchDetail={fetchDetail}
      />
      <PrintModal
        useCustomHook={useCustomHook}
      />
    </PageWrapper>
    {isLoading && (
      <PageLoading
        progressConfig={fetchCount ? {
          total: meta.total_pages,
          finished: fetchCount,
        } : undefined}
      />
    )}
  </>);
};

export default CreateReceiptSubmission;
